import { box, type Box } from './box';

export type AspectPresetId = 'original' | '9:16' | '1:1' | '4:5' | '16:9';

/** Width / height for each preset; `original` is resolved from the source. */
export const ASPECT_RATIOS: Readonly<Record<Exclude<AspectPresetId, 'original'>, number>> = {
  '9:16': 9 / 16,
  '1:1': 1,
  '4:5': 4 / 5,
  '16:9': 16 / 9,
};

export const ASPECT_PRESET_IDS: readonly AspectPresetId[] = ['original', '9:16', '1:1', '4:5', '16:9'];

/** How the source is placed into a frame with a different aspect ratio. */
export type FitMode = 'crop' | 'blurFill';

/** Target size of the output's **short** edge; `source` keeps the original size. */
export type ResolutionId = 'source' | '1080' | '720';

export const RESOLUTION_IDS: readonly ResolutionId[] = ['source', '1080', '720'];

/** Any short side an output can be made at: a choice, or a smaller step only fitting a size limit reaches (`size-target.ts`). */
export type OutputResolution = ResolutionId | '540' | '480' | '360';

export interface Size {
  readonly width: number;
  readonly height: number;
}

const even = (value: number): number => Math.max(2, Math.round(value / 2) * 2);

export function resolveAspect(preset: AspectPresetId, source: Size): number {
  return preset === 'original' ? source.width / source.height : ASPECT_RATIOS[preset];
}

/**
 * Picks the output frame size: the requested aspect, short edge at the requested
 * resolution but never upscaled beyond the source, and even dimensions (H.264).
 *
 * `crop` frames are cut out of the source; `blurFill` frames are built around it,
 * which is why their native size can exceed the source and gets capped to the
 * source's short edge.
 */
export function computeOutputSize(
  source: Size,
  aspect: number,
  resolution: OutputResolution,
  fit: FitMode = 'crop',
  maxLongEdge = 3840,
): Size {
  if (!(source.width > 0 && source.height > 0 && aspect > 0)) throw new RangeError('Invalid size or aspect');
  const widthLimited = source.width / aspect <= source.height;
  // `crop`: largest frame inside the source. `blurFill`: smallest frame containing it.
  const byWidth = fit === 'crop' ? widthLimited : !widthLimited;
  const native: Size = byWidth
    ? { width: source.width, height: source.width / aspect }
    : { width: source.height * aspect, height: source.height };
  const nativeShort = Math.min(native.width, native.height);
  const sourceShort = Math.min(source.width, source.height);
  const requestedShort = resolution === 'source' ? Math.min(nativeShort, sourceShort) : Number(resolution);
  let scale = Math.min(1, requestedShort / nativeShort);
  const long = Math.max(native.width, native.height) * scale;
  if (long > maxLongEdge) scale *= maxLongEdge / long;
  return { width: even(native.width * scale), height: even(native.height * scale) };
}

/**
 * Crop rectangle (source pixels) with the target aspect, centered on `focus`
 * (normalized) and clamped so it never leaves the frame.
 */
export function computeCropRect(source: Size, aspect: number, focus: { x: number; y: number }): Box {
  const sourceAspect = source.width / source.height;
  // Round first so that position + size can never exceed the frame.
  const width = Math.min(source.width, Math.round(aspect < sourceAspect ? source.height * aspect : source.width));
  const height = Math.min(source.height, Math.round(aspect < sourceAspect ? source.height : source.width / aspect));
  const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), Math.max(0, max));
  const x = clamp(focus.x * source.width - width / 2, source.width - width);
  const y = clamp(focus.y * source.height - height / 2, source.height - height);
  return box(Math.round(x), Math.round(y), width, height);
}

/** Rectangle where a `content` of given aspect sits centered and fully visible inside `frame`. */
export function containRect(frame: Size, contentAspect: number): Box {
  const frameAspect = frame.width / frame.height;
  if (contentAspect > frameAspect) {
    const height = frame.width / contentAspect;
    return box(0, (frame.height - height) / 2, frame.width, height);
  }
  const width = frame.height * contentAspect;
  return box((frame.width - width) / 2, 0, width, frame.height);
}

/**
 * Describes where the source image lands on the output frame. Privacy masks and
 * captions use this to convert normalized source coordinates into output pixels.
 */
export interface FrameLayout {
  readonly output: Size;
  /** Source region (source pixels) that is visible. */
  readonly sourceRect: Box;
  /** Destination of `sourceRect` on the output frame (output pixels). */
  readonly destRect: Box;
  readonly fit: FitMode;
}

export interface LayoutOptions {
  readonly aspect: AspectPresetId;
  readonly fit: FitMode;
  readonly resolution: OutputResolution;
  readonly focus: { x: number; y: number };
}

export function computeFrameLayout(source: Size, options: LayoutOptions): FrameLayout {
  const aspect = resolveAspect(options.aspect, source);
  const sameAspect = Math.abs(aspect - source.width / source.height) < 0.01;
  const output = computeOutputSize(source, aspect, options.resolution, sameAspect ? 'crop' : options.fit);
  if (options.fit === 'crop' || sameAspect) {
    return {
      output,
      sourceRect: sameAspect ? box(0, 0, source.width, source.height) : computeCropRect(source, aspect, options.focus),
      destRect: box(0, 0, output.width, output.height),
      fit: options.fit,
    };
  }
  return {
    output,
    sourceRect: box(0, 0, source.width, source.height),
    destRect: containRect(output, source.width / source.height),
    fit: 'blurFill',
  };
}

/** Converts a normalized source box into output pixel coordinates for a given layout. */
export function sourceBoxToOutput(normalized: Box, source: Size, layout: FrameLayout): Box {
  const { sourceRect: s, destRect: d } = layout;
  const sx = d.w / s.w;
  const sy = d.h / s.h;
  return box(
    d.x + (normalized.x * source.width - s.x) * sx,
    d.y + (normalized.y * source.height - s.y) * sy,
    normalized.w * source.width * sx,
    normalized.h * source.height * sy,
  );
}
